// src/pages/work/WorkPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { FiPlus, FiCalendar, FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import useWorkFilters from '../../hooks/useWorkFilters';

import WorkSummaryCards from '../../components/work/WorkSummaryCards'; // smart header (self-fetching)
import WorkFilterBar from '../../components/work/WorkFilterBar';
import WorkViewSwitcher from '../../components/work/WorkViewSwitcher';
import WorkCalendar from '../../components/work/WorkCalendar';
import WorkList from '../../components/work/WorkList';
import AddWorkModal from '../../components/work/AddWorkModal';
import WorkDetailsDrawer from '../../components/work/WorkDetailsDrawer';
import EditWorkModal from '../../components/work/EditWorkModal';
import UnscheduledTray from '../../components/work/UnscheduledTray';

import {
  useWorkByRange,
  invalidateCalendarRange,
} from '../../hooks/useCalendarQueries';

import {
  useWorkList,
  useUpdateWork,
  useTransitionWork,
  useDeleteWork,
} from '../../hooks/useWorkQueries';

/* ───────────────────────────── Constants ───────────────────────────── */

const TABS = ['all', 'tasks', 'jobs', 'appointments'];
const VIEWS = ['calendar', 'list'];

const TYPE_BY_TAB = {
  all: null,
  tasks: 'task',
  jobs: 'job',
  appointments: 'appointment',
};

const LS_VIEW_KEY = (tab) => `work.view.${tab}`;
const LS_CALVIEW_KEY = (tab) => `work.calview.${tab}`;

/* ───────────────────────────── Utilities ───────────────────────────── */

const DBG = String(process.env.REACT_APP_CALENDAR_DEBUG || '').toLowerCase() === 'true';

function dlog(label, obj) {
  if (DBG) console.debug(`[WorkPage] ${label}`, obj ?? '');
}
function track(event, props) {
  try {
    const a = (typeof window !== 'undefined' && window.analytics) || null;
    if (a && typeof a.track === 'function') a.track(event, props || {});
    else if (process.env.NODE_ENV !== 'production') console.debug('[telemetry]', event, props);
  } catch (_) {}
}
function deriveTabFromPath(pathname) {
  if (pathname.startsWith('/work/jobs')) return 'jobs';
  if (pathname.startsWith('/work/schedule')) return 'appointments';
  if (pathname.startsWith('/work/tasks')) return 'tasks';
  return 'all';
}
const fmt = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

/* ─────────────────────────── Main Component ─────────────────────────── */

export default function WorkPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageRootRef = useRef(null);
  const queryClient = useQueryClient();

  // Mount/Unmount logs
  useEffect(() => {
    dlog('mount', { path: location.pathname, search: searchParams.toString() });
    return () => dlog('unmount');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global filters (SSOT)
  const { filters, setFilters } = useWorkFilters();

  // Derive active tab from filters first (SSOT → UI), then fall back to URL/path
  const urlTab = searchParams.get('tab') || deriveTabFromPath(location.pathname);

  const tabFromFilters = useMemo(() => {
    const t = filters?.type;
    if (t === null) return 'all';
    if (Array.isArray(t) && t.length === 1) {
      if (t[0] === 'task') return 'tasks';
      if (t[0] === 'job') return 'jobs';
      if (t[0] === 'appointment') return 'appointments';
    }
    return null; // when mixed/undefined → fallback to URL
  }, [filters?.type]);

  const tab = useMemo(() => {
    const base = TABS.includes(urlTab) ? urlTab : deriveTabFromPath(location.pathname);
    return tabFromFilters || base || 'all';
  }, [tabFromFilters, urlTab, location.pathname]);

  // 1) Primary view, persisted per tab (URL overrides storage)
  const storedView = localStorage.getItem(LS_VIEW_KEY(tab)) || undefined;
  const urlView = searchParams.get('view') || undefined;
  const defaultViewByTab = tab === 'tasks' ? 'list' : 'calendar';
  const view = VIEWS.includes(urlView || '')
    ? (urlView || storedView || defaultViewByTab)
    : (storedView || defaultViewByTab);

  // Calendar subview (month|week|day) stored per-tab
  const storedCalView = localStorage.getItem(LS_CALVIEW_KEY(tab)) || 'month';
  const [calView, setCalView] = useState(storedCalView);
  useEffect(() => localStorage.setItem(LS_CALVIEW_KEY(tab), calView), [tab, calView]);

  // Log URL/search param changes (non-filter)
  useEffect(() => {
    if (!DBG) return;
    dlog('url.searchParams', searchParams.toString());
  }, [searchParams]);

  // 2) Seed current month ONLY for calendar view and ONLY if no dates present
  useEffect(() => {
    if (view !== 'calendar') return;
    const hasDates = Boolean(filters.from && filters.to);
    if (hasDates) return;

    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    dlog('calendar.autoSeed.currentMonth', { from: fmt(start), to: fmt(end) });
    setFilters({ from: fmt(start), to: fmt(end) });
  }, [view, filters.from, filters.to, setFilters]);

  // 3) List view paging/sort in URL (not part of filter SSOT)
  const pageNum = Number(searchParams.get('page') || 1);
  const limitNum = Number(searchParams.get('limit') || 25);
  const sortParam = searchParams.get('sort') || undefined;

  const listSortObj = useMemo(() => {
    if (!sortParam) return undefined;
    const [field, dirRaw] = String(sortParam).split(':');
    const dir = dirRaw === 'asc' ? 'asc' : 'desc';
    return { field, dir };
  }, [sortParam]);

  /* 4) DATA FETCHING */

  // List
  const listQ = useWorkList({ ...filters, page: pageNum, limit: limitNum, sort: sortParam });
  useEffect(() => {
    if (!DBG) return;
    dlog('list.query', {
      loading: listQ.isLoading,
      fetching: listQ.isFetching,
      error: listQ.error?.message,
      count: Array.isArray(listQ.items) ? listQ.items.length : 0,
      page: pageNum,
      limit: limitNum,
      sort: sortParam,
    });
  }, [listQ.isLoading, listQ.isFetching, listQ.error, listQ.items, pageNum, limitNum, sortParam]);

  // 🔧 Normalize search aliases once and reuse everywhere
  const normalizedQ = useMemo(() => {
    const first = [filters.q, filters.search, filters.title, filters.query, filters.keyword, filters.term]
      .find((v) => typeof v === 'string' && v.trim() !== '');
    return first ? first.trim() : undefined;
  }, [filters.q, filters.search, filters.title, filters.query, filters.keyword, filters.term]);

  // Calendar: build params from SSOT + range
  const calendarParams = useMemo(() => {
    const p = {
      from: filters.from,                  // 'YYYY-MM-DD'
      to: filters.to,                      // 'YYYY-MM-DD'
      q: normalizedQ,
      type: filters.type || undefined,
      status: filters.status || undefined,
      priority: filters.priority || undefined,
      assigneeId: filters.assigneeId || undefined,
      clientId: filters.clientId || undefined,
      assigneeName: filters.assigneeName || undefined,
      clientName: filters.clientName || undefined,
      includeCancelled: !!filters.includeCancelled,
    };
    dlog('calendar.params', p);
    return p;
  }, [filters, normalizedQ]);

  const calendarQ = useWorkByRange(calendarParams, {
    enabled: Boolean(filters.from && filters.to),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!DBG) return;
    dlog('calendar.query', {
      enabled: Boolean(filters.from && filters.to),
      loading: calendarQ.isLoading,
      fetching: calendarQ.isFetching,
      error: calendarQ.error?.message,
      items: Array.isArray(calendarQ.items) ? calendarQ.items.length : 0,
      from: filters.from,
      to: filters.to,
      q: normalizedQ,
    });
  }, [calendarQ.isLoading, calendarQ.isFetching, calendarQ.error?.message, calendarQ.items, filters.from, filters.to, normalizedQ]);

  useEffect(() => {
    if (!DBG) return;
    dlog('calendar.items.sample', { count: calendarQ.items?.length || 0, sample: (calendarQ.items || []).slice(0, 3) });
  }, [calendarQ.items]);

  // Unscheduled feed (fetch *always*; only the tray UI is gated by view)
  const unscheduledParams = {
    ...filters,
    from: undefined,
    to: undefined,
    page: 1,
    limit: 200,
    sort: 'createdAt:desc',
    onlyUnscheduled: 1,
  };
  const unscheduledFetch = useWorkList(unscheduledParams);
  const unscheduledAll = (unscheduledFetch.items || []).filter((it) => !it?.schedule?.start);

  // 6) Mutations
  const updateWork = useUpdateWork();
  const transitionWork = useTransitionWork();
  const deleteWork = useDeleteWork();

  // 7) URL helper for non-filter params (tab/view/page/limit/sort)
  const setUrlParams = useCallback(
    (patch, { replace = false } = {}) => {
      // ✅ Preserve ALL existing params; only set/remove the keys we manage
      const next = new URLSearchParams(searchParams);
      const ALLOW = new Set(['tab', 'view', 'page', 'limit', 'sort']);
      Object.entries(patch || {}).forEach(([k, v]) => {
        if (!ALLOW.has(k)) return;
        if (v == null || (typeof v === 'string' && v.trim() === '')) next.delete(k);
        else next.set(k, String(v));
      });
      dlog('url.set', { patch, replace, next: next.toString() });
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams]
  );

  // Persist primary view per tab
  useEffect(() => {
    localStorage.setItem(LS_VIEW_KEY(tab), view);
    dlog('view.persist', { tab, view });
  }, [tab, view]);

  // 8) Invalidation helpers
  const refetchAll = useCallback(() => {
    dlog('refetchAll', 'triggered');
    listQ.refetch?.();
    unscheduledFetch.refetch?.();
    if (view === 'calendar') {
      const key = {
        from: filters.from,
        to: filters.to,
        q: normalizedQ,
        status: filters.status,
        priority: filters.priority,
        type: filters.type,
        assigneeId: filters.assigneeId,
        clientId: filters.clientId,
        assigneeName: filters.assigneeName,
        clientName: filters.clientName,
        includeCancelled: filters.includeCancelled,
      };
      invalidateCalendarRange(queryClient, key);
    }
  }, [listQ, unscheduledFetch, view, filters, normalizedQ, queryClient]);

  const invalidateCal = useCallback(() => {
    const key = {
      from: filters.from,
      to: filters.to,
      q: normalizedQ,
      status: filters.status,
      priority: filters.priority,
      type: filters.type,
      assigneeId: filters.assigneeId,
      clientId: filters.clientId,
      assigneeName: filters.assigneeName,
      clientName: filters.clientName,
      includeCancelled: filters.includeCancelled,
    };
    dlog('calendar.invalidate', key);
    invalidateCalendarRange(queryClient, key);
  }, [queryClient, filters, normalizedQ]);

  // 9) Local UI state
  const [addOpen, setAddOpen] = useState(false);
  const [scheduleSeed, setScheduleSeed] = useState(undefined);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(undefined);

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(undefined);

  const [trayVisible, setTrayVisible] = useState(true);

  // 10) Handlers

  // FilterBar → directly update SSOT
  const handleFilterChange = useCallback(
    (next) => {
      dlog('filters.change', next);
      setFilters(next);
      track('work.filters.changed', next);
    },
    [setFilters]
  );

  const handleCalSubviewChange = useCallback(
    (v) => {
      dlog('calendar.subview.change', { from: calView, to: v });
      setCalView(v);
      localStorage.setItem(LS_CALVIEW_KEY(tab), v);
    },
    [tab, calView]
  );

  // Calendar range (YYYY-MM-DD) → SSOT so queries stay in sync
  const handleRangeChange = useCallback(
    ({ from, to }) => {
      if (!from || !to) return;
      dlog('calendar.range.change', { from, to });
      setFilters({ from, to });
    },
    [setFilters]
  );

  // Create/move/resize/schedule
  const handleCreateFromCalendar = useCallback(({ start, end, allDay }) => {
    dlog('calendar.create.intent', { start, end, allDay });
    setScheduleSeed({
      start,
      end,
      allDay,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    setAddOpen(true);
    track('work.calendar.create.intent', { start, end, allDay });
  }, []);

  const handleMove = useCallback(
    async ({ id, start, end, allDay }) => {
      dlog('calendar.move', { id, start, end, allDay });
      try {
        await updateWork.mutateAsync({
          id,
          patch: { schedule: { start, end, allDay, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } },
        });
        toast.success('Schedule updated');
        dlog('calendar.move.success', { id });
        invalidateCal();
        unscheduledFetch.refetch?.();
      } catch (err) {
        dlog('calendar.move.error', { id, error: err?.message });
        toast.error(err?.message || 'Failed to update schedule');
      }
    },
    [updateWork, invalidateCal, unscheduledFetch]
  );

  const handleResize = useCallback(
    async ({ id, start, end, allDay }) => {
      dlog('calendar.resize', { id, start, end, allDay });
      try {
        await updateWork.mutateAsync({
          id,
          patch: { schedule: { start, end, allDay, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } },
        });
        toast.success('Duration updated');
        dlog('calendar.resize.success', { id });
        invalidateCal();
        unscheduledFetch.refetch?.();
      } catch (err) {
        dlog('calendar.resize.error', { id, error: err?.message });
        toast.error(err?.message || 'Failed to update duration');
      }
    },
    [updateWork, invalidateCal, unscheduledFetch]
  );

  const handleScheduleFromTray = useCallback(
    async ({ id, start, end, allDay }) => {
      dlog('tray.schedule.drop', { id, start, end, allDay });
      try {
        await updateWork.mutateAsync({
          id,
          patch: { schedule: { start, end, allDay, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } },
        });
        toast.success('Item scheduled');
        dlog('tray.schedule.success', { id });
        invalidateCal();
        unscheduledFetch.refetch?.();
      } catch (err) {
        dlog('tray.schedule.error', { id, error: err?.message });
        toast.error(err?.message || 'Failed to schedule item');
      }
    },
    [updateWork, invalidateCal, unscheduledFetch]
  );

  const handleTrayQuickSchedule = useCallback((work) => {
    const id = work?._id || work?.id;
    if (!id) return;
    dlog('tray.quickSchedule.openEdit', { id });
    setEditId(String(id));
    setEditOpen(true);
  }, []);

  // Details drawer
  const openDetails = useCallback((workOrId) => {
    const id =
      typeof workOrId === 'object' && workOrId
        ? (workOrId._id || workOrId.id)
        : workOrId;
    if (!id) return;
    const strId = String(id);
    dlog('details.open', { id: strId });
    setDrawerId(strId);
    setDrawerOpen(true);
  }, []);
  const closeDetails = useCallback(() => {
    dlog('details.close');
    setDrawerOpen(false);
    setDrawerId(undefined);
  }, []);

  const handleNewClick = useCallback(() => {
    dlog('newWork.click');
    setScheduleSeed(undefined);
    setAddOpen(true);
  }, []);

  const handleDeleted = useCallback(
    async (item) => {
      const id = item?._id || item?.id;
      if (!id) return;
      const title = item?.title || 'this item';
      const ok = window.confirm(`Delete ${title}? This cannot be undone.`);
      if (!ok) return;
      dlog('delete.intent', { id, title });
      try {
        await deleteWork.mutateAsync(id);
        toast.success('Deleted');
        dlog('delete.success', { id });
        refetchAll();
      } catch (err) {
        dlog('delete.error', { id, error: err?.message });
        toast.error(err?.message || 'Failed to delete');
      }
    },
    [deleteWork, refetchAll]
  );

  const handleEdit = useCallback((item) => {
    const id = item?._id || item?.id;
    if (!id) return;
    dlog('edit.open', { id });
    setEditId(String(id));
    setEditOpen(true);
  }, []);

  const handleListSortChange = useCallback(
    ({ field, dir }) => {
      const sort = field ? `${field}:${dir === 'asc' ? 'asc' : 'desc'}` : undefined;
      dlog('list.sort.change', { sort, field, dir });
      setUrlParams({ sort, page: 1 });
    },
    [setUrlParams]
  );

  // View / Tab switchers
  function handleTabChange(nextTab) {
    const nextType = TYPE_BY_TAB[nextTab];
    const nextView =
      localStorage.getItem(LS_VIEW_KEY(nextTab)) ||
      (nextTab === 'tasks' ? 'list' : 'calendar');

    dlog('tab.change', { from: tab, to: nextTab, nextView, nextType });
    setUrlParams({ tab: nextTab, view: nextView, page: undefined });

    if (nextType) setFilters({ type: [nextType] });
    else setFilters({ type: null });

    // If switching into a list-first tab, also clear any lingering date window
    if (nextView === 'list') setFilters({ from: null, to: null });
  }

  function handleViewChange(nextView) {
    dlog('view.change', { from: view, to: nextView });
    localStorage.setItem(LS_VIEW_KEY(tab), nextView);
    setUrlParams({ view: nextView, page: undefined });

    // 🔑 Hard rule: whenever switching to LIST, drop the calendar date window
    if (nextView === 'list') {
      setFilters({ from: null, to: null });
    }
  }

  /* ───────────────────────────── Rendering ───────────────────────────── */

  // Expose some quick debug knobs on window (DEV only)
  useEffect(() => {
    if (!DBG) return;
    window.__workDbg = {
      tab,
      view,
      filters,
      calendarParams,
      calendarItems: calendarQ.items,
      listItems: listQ.items,
      invalidateCal,
      refetchAll,
      search: searchParams.toString(),
    };
    dlog('window.__workDbg ready', Object.keys(window.__workDbg));
  }, [tab, view, filters, calendarParams, calendarQ.items, listQ.items, invalidateCal, refetchAll, searchParams]);

  const trayItems = view === 'calendar' ? unscheduledAll : []; // UI gating only

  return (
    <div ref={pageRootRef} className="w-full min-h-[90vh] py-6 px-3 sm:px-6 lg:px-8 bg-slate-50">
      {/* Header Card (compact) */}
      <div className="rounded-2xl mb-4 border border-slate-200 bg-white shadow-sm">
        <div className="p-4 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <FiCalendar />
                </div>
                <h1 className="text-2xl font-bold text-slate-900">Work</h1>
              </div>
              <p className="text-slate-600 mt-0.5">Unified tasks, jobs, and schedule — one hub.</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleNewClick}
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-1.5 text-sm rounded-xl font-semibold shadow hover:bg-black/90 transition"
              >
                <FiPlus className="text-base" /> New Work
              </button>
            </div>
          </div>

          {/* Quick Summary (smart; self-fetching) */}
          <div className="mt-3">
            <WorkSummaryCards
              compact
              onOpenDetails={openDetails}
              onShowUnscheduled={() => setTrayVisible(true)}
              // optional tuning until true crew hours exist
              capacityHoursPerTechDay={8}
              defaultDurations={{ task: 60, job: 90, appointment: 60 }}
            />
          </div>
        </div>
      </div>

      {/* Filter Bar (compact) */}
      <div className="mb-4">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-3">
          <WorkFilterBar
            value={{
              q: normalizedQ || '',
              status: filters.status || [],
              priority: filters.priority || [],
              type: filters.type || [],
              from: filters.from || '',
              to: filters.to || '',
              assigneeId: filters.assigneeId || [],
              clientId: filters.clientId || [],
              assigneeName: filters.assigneeName || '',
              clientName: filters.clientName || '',
              includeCancelled: !!filters.includeCancelled,
            }}
            onChange={handleFilterChange}
            onOpenMoreFilters={() => track('work.filters.openMore')}
          />
        </div>
      </div>

      {/* Primary Canvas Card */}
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Top row: tab + primary view switcher + tray toggle (calendar only) */}
        <div className="px-4 pt-4 pb-2 border-b border-slate-100 flex items-center justify-between">
          <WorkViewSwitcher
            tab={tab}
            onTabChange={handleTabChange}
            view={view}
            onViewChange={handleViewChange}
            views={['calendar', 'list']}
          />
          {view === 'calendar' && (
            <button
              type="button"
              onClick={() => {
                dlog('tray.toggle', { from: trayVisible, to: !trayVisible });
                setTrayVisible((v) => !v);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              title={trayVisible ? 'Hide Unscheduled' : 'Show Unscheduled'}
            >
              {trayVisible ? <FiEyeOff /> : <FiEye />}
              {trayVisible ? 'Hide Unscheduled' : 'Show Unscheduled'}
            </button>
          )}
        </div>

        {/* Main content */}
        <div className="p-4">
          {view === 'calendar' && (
            trayVisible ? (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(220px,280px)]">
                <div className="min-w-0">
                  <WorkCalendar
                    view={calView}
                    onViewChange={handleCalSubviewChange}
                    onRangeChange={handleRangeChange}
                    items={calendarQ.items || []}
                    isLoading={calendarQ.isLoading}
                    isFetching={calendarQ.isFetching}
                    readOnly={false}
                    onCreate={handleCreateFromCalendar}
                    onMove={handleMove}
                    onResize={handleResize}
                    onExternalDrop={handleScheduleFromTray}
                    onOpenDetails={openDetails}
                  />
                </div>

                <aside className="min-w-[220px]">
                  <UnscheduledTray
                    items={view === 'calendar' ? unscheduledAll : []}
                    isFetching={unscheduledFetch.isFetching}
                    isLoading={unscheduledFetch.isLoading}
                    error={unscheduledFetch.error}
                    onRefresh={() => {
                      dlog('unscheduled.refresh.click');
                      unscheduledFetch.refetch?.();
                    }}
                    onOpenDetails={openDetails}
                    onCreateSchedule={handleTrayQuickSchedule}
                    dragEnabled={true}
                    defaultDurations={{ task: 60, job: 90, appointment: 60 }}
                    initialCollapsed={false}
                    className="max-w-[280px] mx-auto"
                  />
                </aside>
              </div>
            ) : (
              <WorkCalendar
                view={calView}
                onViewChange={handleCalSubviewChange}
                onRangeChange={handleRangeChange}
                items={calendarQ.items || []}
                isLoading={calendarQ.isLoading}
                isFetching={calendarQ.isFetching}
                readOnly={false}
                onCreate={handleCreateFromCalendar}
                onMove={handleMove}
                onResize={handleResize}
                onExternalDrop={handleScheduleFromTray}
                onOpenDetails={openDetails}
              />
            )
          )}

          {view === 'list' && (
            <WorkList
              type={(filters.type && filters.type[0]) || null}
              items={listQ.items || []}
              meta={listQ.meta}
              isLoading={listQ.isLoading}
              isFetching={listQ.isFetching}
              error={listQ.error}
              sort={listSortObj}
              onSortChange={handleListSortChange}
              onOpenDetails={openDetails}
              onEdit={handleEdit}
              onDelete={handleDeleted}
              onTransition={async (item, nextStatus) => {
                const id = item?._id || item?.id;
                dlog('list.transition', { id, nextStatus });
                try {
                  await transitionWork.mutateAsync({
                    id,
                    status: nextStatus,
                  });
                  toast.success('Status updated');
                  dlog('list.transition.success', { id, nextStatus });
                  refetchAll();
                } catch (err) {
                  dlog('list.transition.error', { id, error: err?.message });
                  toast.error(err?.message || 'Failed to update status');
                }
              }}
              onPageChange={(nextPage) => {
                dlog('list.page.change', { from: pageNum, to: nextPage });
                setUrlParams({ page: nextPage });
              }}
            />
          )}
        </div>
      </div>

      {/* Details Drawer */}
      {drawerOpen && drawerId && (
        <WorkDetailsDrawer
          key={drawerId}
          open={drawerOpen}
          workId={drawerId}
          onClose={closeDetails}
          onUpdated={() => {
            toast.success('Updated');
            dlog('details.updated', { id: drawerId });
            closeDetails();
            refetchAll();
          }}
        />
      )}

      {/* Edit Modal */}
      {editOpen && editId && (
        <EditWorkModal
          key={editId}
          open={editOpen}
          workId={editId}
          onClose={() => {
            dlog('edit.close', { id: editId });
            setEditOpen(false);
            setEditId(undefined);
          }}
          onUpdated={() => {
            dlog('edit.updated', { id: editId });
            setEditOpen(false);
            setEditId(undefined);
            toast.success('Work updated');
            refetchAll();
          }}
        />
      )}

      {/* Add Modal */}
      {addOpen && (
        <AddWorkModal
          open={addOpen}
          onClose={() => {
            dlog('add.close');
            setAddOpen(false);
            setScheduleSeed(undefined);
          }}
          onCreated={() => {
            dlog('add.created');
            setAddOpen(false);
            setScheduleSeed(undefined);
            toast.success('Work created');
            refetchAll();
          }}
          seed={{
            type: TYPE_BY_TAB[tab],
            schedule: scheduleSeed,
          }}
        />
      )}
    </div>
  );
}
